"use client";

import { pxToRem } from "@ui/lib/utils";
import { clsx } from "clsx";
import { memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Radio, RadioGroup, type Key } from "react-aria-components";
import { mergeProps, useFocusVisible, useFocusWithin, useHover } from "react-aria";
import { TextEditor } from "../../TextEditor";
import { useTtsGeneration } from "@ui/hooks/useTtsGeneration";
import { TextHighlighter } from "../../TextHighlighter";
import { Select } from "../../Select";
import { useTranslations } from "next-intl";
import { TTSModels } from "@mw/shared/models";
import { LimitReachedOverlay } from "../../LimitReachedOverlay";
import { ttsLanguages as languages, getLanguageDisplayLabel } from "@components/languages/config";
import { getDynamicFlagURL } from "@ui/components/languages/DynamicFlag";
import { useSpinDelay } from "spin-delay";
import { getDefaultPrompt } from "@ui/components/languages/textPrompts";
import { EventName } from "@mw/shared/analyticsEventNames";
import { logger } from "@utilities/logger";
import { captureEvent } from "@/app/_analytics/analytics";
import { type VoiceWithPreview } from "@ui/components/demos/VoicesWithTTS/types";
import { getVoicesWithPreview } from "@/app/_api/fetchVoices";
import { getVoiceAvatarImage, VoiceAvatar } from "@ui/components/demos/VoicesWithTTS/VoiceAvatar";
import { NativeAudioProvider, useNativeAudio } from "@/app/_providers/native-audio";
import { DownloadButton } from "@ui/components/demos/TTS/components/TTSControls";
import { Footer } from "../../Footer";
import { appUrls } from "@/constants";
import { BaseLinkTracked } from "@ui/components/core/LinkTracked";

interface SpeechProps {
  initialVoices: VoiceWithPreview[];
  initialLanguage: string;
  initialText: string;
  variant?: "default" | "mini";
  size?: "sm" | "lg";
  hideExploreLinks?: boolean;
  showDownloadButton?: boolean;
  onAfterDownload?: () => void;
  defaultMobilePanel?: "voices" | "editor";
  initialSelectedVoiceId?: string;
  ttsBaseUrlOverride?: string;
  ttsAdditionalHeaders?: Record<string, string>;
}

function sortLanguagesWithEnglishFirst(a: (typeof languages)[0], b: (typeof languages)[0]) {
  if (a.value === "en") return -1;
  if (b.value === "en") return 1;
  return a.label.localeCompare(b.label);
}

function VoiceRadio({
  ref,
  voice,
  voices,
  index,
  voiceAvatarSeed,
  manualPauseRef,
  selectedVoiceId,
  onPlayPreview,
}: {
  ref?: React.RefObject<HTMLLabelElement | null>;
  voice: VoiceWithPreview;
  voices: VoiceWithPreview[];
  index: number;
  voiceAvatarSeed: string;
  manualPauseRef: React.RefObject<boolean>;
  selectedVoiceId: Key;
  onPlayPreview: (voice: VoiceWithPreview) => void;
}) {
  const t = useTranslations("ClientKeys.TTS");
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const { isHovered, hoverProps } = useHover({});
  const { isFocusVisible } = useFocusVisible();
  const { focusWithinProps } = useFocusWithin({
    onFocusWithinChange(isFocusWithin) {
      setIsFocusWithin(isFocusWithin);
    },
  });
  const isFocused = isHovered || (isFocusWithin && isFocusVisible);
  const [name, description] = voice.name.split(/\s+-\s+/);
  const { togglePlay, playingId } = useNativeAudio();
  const isPlaying = playingId === voice.previewUrl;
  const isSelected = selectedVoiceId === voice.voiceId;
  const previewButtonVisible = isFocused || isPlaying || isSelected;
  const isAtTop = index % 5 === 0;
  const isAtBottom = index % 5 === 4 || index === voices.length - 1;

  return (
    <div {...mergeProps(focusWithinProps, hoverProps, { className: clsx("tw-w-full tw-relative tw-snap-start") })}>
      <Radio
        ref={ref}
        key={voice.voiceId}
        value={voice.voiceId}
        className='tw-block tw-cursor-pointer tw-px-3 tw-whitespace-nowrap'
        style={{
          // Using 11px padding here because the hover background is inset by 1px vertically
          paddingTop: isAtTop ? pxToRem(11) : undefined,
          paddingBottom: isAtBottom ? pxToRem(11) : undefined,
        }}
        onClick={() => {
          if (isPlaying) {
            manualPauseRef.current = true;
            togglePlay({ id: voice.previewUrl, url: voice.previewUrl });
          } else if (!manualPauseRef.current) {
            onPlayPreview(voice);
            togglePlay({ id: voice.previewUrl, url: voice.previewUrl });
          }
        }}
      >
        {({ isSelected, isFocusVisible }) => (
          <div
            className={clsx(
              "tw-relative tw-isolate tw-rounded-xl tw-h-12 tw-flex tw-items-center tw-pl-3 tw-pr-4 tw-gap-2.5",
              isFocusVisible && "tw-outline-focus tw-relative tw-z-10",
            )}
          >
            <div
              className={clsx(
                "tw-absolute -tw-z-10 tw-rounded-xl tw-inset-x-0 tw-inset-y-0.25 tw-bg-cream",
                !isHovered && !isSelected && "tw-opacity-0",
              )}
            />
            <div className='tw-relative tw-flex-none'>
              <VoiceAvatar
                index={index}
                seed={voiceAvatarSeed}
                className='tw-size-5 tw-rounded-full'
                width={20}
                height={20}
              />
              {isSelected && (
                <div className='tw-absolute -tw-top-1 -tw-right-1 tw-size-3 tw-rounded-full tw-bg-black tw-ring-2 tw-ring-cream'>
                  <svg fill='none' viewBox='0 0 12 12' className='tw-size-3' aria-hidden>
                    <path
                      stroke='#fff'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth='1.5'
                      d='m4 6.4 1.2 1.2L8 4.4'
                    />
                  </svg>
                </div>
              )}
            </div>
            <div className='tw-flex-auto tw-min-w-0 tw-flex tw-items-baseline tw-justify-between tw-gap-3'>
              <div className='tw-text-sm/5 tw-font-medium tw-text-black'>{name}</div>
              {description && (
                <div
                  className={clsx(
                    "tw-text-xs/5 tw-font-medium tw-text-gray-500 tw-truncate",
                    previewButtonVisible && "tw-invisible",
                  )}
                >
                  {description}
                </div>
              )}
            </div>
          </div>
        )}
      </Radio>
      <button
        type='button'
        className={clsx(
          "tw-absolute tw-z-20 tw-right-5 tw-bg-white tw-text-black tw-rounded-full tw-outline-none focus-visible:tw-outline-focus tw-flex tw-items-center tw-justify-center tw-size-9",
          previewButtonVisible ? "" : "tw-invisible",
        )}
        style={{
          top: isAtTop ? pxToRem(17) : pxToRem(6),
          boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 4px 4px rgb(0 0 0 / 0.04)",
        }}
        onClick={() => {
          if (isPlaying) {
            manualPauseRef.current = true;
          } else {
            onPlayPreview(voice);
          }
          togglePlay({ id: voice.previewUrl, url: voice.previewUrl });
        }}
        aria-label={isPlaying ? t("widget.pause") : t("widget.preview")}
      >
        <svg viewBox='0 0 24 24' aria-hidden className='tw-size-3.5'>
          {isPlaying ? (
            <>
              <path
                fill='currentColor'
                d='M6.75 3A2.75 2.75 0 0 0 4 5.75v12.5A2.75 2.75 0 0 0 6.75 21h.5A2.75 2.75 0 0 0 10 18.25V5.75A2.75 2.75 0 0 0 7.25 3z'
              />
              <path
                fill='currentColor'
                d='M16.75 3A2.75 2.75 0 0 0 14 5.75v12.5A2.75 2.75 0 0 0 16.75 21h.5A2.75 2.75 0 0 0 20 18.25V5.75A2.75 2.75 0 0 0 17.25 3z'
              />
            </>
          ) : (
            <path
              fill='currentColor'
              d='M9.244 2.368C7.414 1.184 5 2.497 5 4.676v14.648c0 2.18 2.414 3.493 4.244 2.309l11.318-7.324c1.675-1.084 1.675-3.534 0-4.618z'
            />
          )}
        </svg>
      </button>
    </div>
  );
}

const VoiceSelector = memo(function VoiceSelector({
  initialFocusRef,
  isPending,
  isLoading,
  selectedVoiceId,
  voices,
  voiceAvatarSeed,
  onForward,
  onPlayPreview,
  onChangeVoice,
  hideExploreLinks,
  defaultMobilePanel,
}: {
  initialFocusRef: React.RefObject<HTMLLabelElement | null>;
  isPending: boolean;
  isLoading: boolean;
  selectedVoiceId: string;
  voices: VoiceWithPreview[];
  voiceAvatarSeed: string;
  onForward: () => void;
  onPlayPreview: (voice: VoiceWithPreview) => void;
  onChangeVoice: (voiceId: string) => void;
  hideExploreLinks?: boolean;
  defaultMobilePanel?: SpeechProps["defaultMobilePanel"];
}) {
  const t = useTranslations("ClientKeys.Globals");
  const tTTS = useTranslations("ClientKeys.TTS");
  const tTTSVoices = useTranslations("ClientKeys.VoicesWithTTS");
  const manualPauseRef = useRef(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  function checkScroll() {
    if (!scrollRef.current) return;
    setCanScrollLeft(scrollRef.current?.scrollLeft > 0);
    setCanScrollRight(scrollRef.current?.scrollLeft < scrollRef.current?.scrollWidth - scrollRef.current?.clientWidth);
  }

  useLayoutEffect(() => {
    scrollRef.current?.scrollTo({ left: 0, behavior: "instant" });
    checkScroll();
  }, [voices]);

  return (
    <div
      inert={isLoading || isPending}
      className={clsx(
        "tw-snap-start tw-flex-none md:tw-w-1/2 tw-relative tw-z-10 tw-h-[20rem] tw-flex-col",
        defaultMobilePanel === "editor" ? "tw-hidden md:tw-flex" : "tw-w-full tw-flex",
      )}
    >
      <RadioGroup
        ref={scrollRef}
        aria-label={tTTS("widget.voice")}
        value={selectedVoiceId}
        onChange={onChangeVoice}
        className={clsx(
          "tw-h-72 tw-flex tw-flex-col tw-flex-wrap tw-overflow-x-auto tw-scrollbar-none tw-snap-x tw-snap-mandatory",
          isPending && "tw-opacity-50 tw-transition",
        )}
        onScroll={() => {
          checkScroll();
        }}
      >
        {voices.map((voice, voiceIndex) => (
          <VoiceRadio
            ref={selectedVoiceId === voice.voiceId ? initialFocusRef : undefined}
            key={voice.voiceId}
            index={voiceIndex}
            voice={voice}
            voices={voices}
            voiceAvatarSeed={voiceAvatarSeed}
            selectedVoiceId={selectedVoiceId}
            manualPauseRef={manualPauseRef}
            onPlayPreview={onPlayPreview}
          />
        ))}
      </RadioGroup>
      <div className='tw-flex tw-px-3 tw-pb-5 md:tw-pb-3'>
        {!hideExploreLinks && (
          <BaseLinkTracked
            href={appUrls.voiceLibrary}
            eventName={EventName.mw_tts_voice_library_explore_clicked}
            className='tw-bg-white hover:tw-bg-cream tw-text-black tw-rounded-full tw-outline-none focus-visible:tw-outline-focus tw-text-sm/none tw-h-9 tw-px-3.5 tw-items-center tw-justify-center tw-hidden md:tw-flex'
            style={{ boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 4px 4px rgb(0 0 0 / 0.04)" }}
          >
            {tTTS("widget.cta")}
          </BaseLinkTracked>
        )}
        <button
          type='button'
          className='tw-bg-white hover:tw-bg-cream tw-text-black tw-rounded-full tw-outline-none focus-visible:tw-outline-focus tw-text-sm/none tw-h-9 tw-px-3.5 tw-items-center tw-justify-center tw-min-w-0 tw-flex md:tw-hidden'
          style={{ boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 4px 4px rgb(0 0 0 / 0.04)" }}
          onClick={onForward}
        >
          <span className='tw-truncate'>{tTTSVoices("edit_text")}</span>
        </button>
        <button
          type='button'
          aria-label={t("previous")}
          className={clsx(
            "tw-flex-none tw-rounded-full tw-outline-none focus-visible:tw-outline-focus tw-size-9 tw-ml-auto tw-flex tw-items-center tw-justify-center",
            isPending && "tw-transition",
            canScrollLeft && !isPending ? "tw-text-gray-500 hover:tw-text-black hover:tw-bg-cream" : "tw-text-gray-300",
          )}
          onClick={() => {
            if (!scrollRef.current) return;
            scrollRef.current.scrollBy({ left: -scrollRef.current.offsetWidth, behavior: "smooth" });
            captureEvent(EventName.mw_tts_prev_next_voices, { direction: "previous" });
          }}
          disabled={!canScrollLeft}
        >
          <svg fill='none' viewBox='0 0 24 24' aria-hidden className='tw-size-6'>
            <path
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='1.5'
              d='m13.586 16-3.293-3.293a1 1 0 0 1 0-1.414L13.586 8'
            />
          </svg>
        </button>
        <button
          type='button'
          aria-label={t("next")}
          className={clsx(
            "tw-flex-none tw-rounded-full tw-outline-none focus-visible:tw-outline-focus tw-size-9 tw-flex tw-items-center tw-justify-center",
            isPending && "tw-transition",
            canScrollRight && !isPending
              ? "tw-text-gray-500 hover:tw-text-black hover:tw-bg-cream"
              : "tw-text-gray-300",
          )}
          onClick={() => {
            if (!scrollRef.current) return;
            scrollRef.current.scrollBy({ left: scrollRef.current.offsetWidth, behavior: "smooth" });
            captureEvent(EventName.mw_tts_prev_next_voices, { direction: "next" });
          }}
          disabled={!canScrollRight}
        >
          <svg fill='none' viewBox='0 0 24 24' aria-hidden className='tw-size-6'>
            <path
              stroke='currentColor'
              strokeLinecap='round'
              strokeLinejoin='round'
              strokeWidth='1.5'
              d='m10 16 3.293-3.293a1 1 0 0 0 0-1.414L10 8'
            />
          </svg>
        </button>
      </div>
    </div>
  );
});

const PromptEditor = memo(function PromptEditor({
  initialFocusRef,
  handleRef,
  onBack,
  initialText,
  variant = "default",
  size = "sm",
  defaultMobilePanel,
  selectedVoiceId,
  selectedLanguage,
  prompt,
  onChangePrompt,
  isLoading,
  isPending,
  onChangeLanguage: onChangeLanguageProp,
  onChangeVoice: onChangeVoiceProp,
  voices,
  voiceAvatarSeed,
  stopPreview,
  showDownloadButton,
  onAfterDownload,
  ttsBaseUrlOverride,
  ttsAdditionalHeaders,
}: {
  initialFocusRef?: React.RefObject<HTMLButtonElement | null>;
  handleRef?: React.RefObject<{ pause: () => void; setRegenerate: (regenerate: boolean) => void } | null>;
  onBack?: () => void;
  initialText: string;
  variant?: "default" | "mini";
  size?: "sm" | "lg";
  defaultMobilePanel?: SpeechProps["defaultMobilePanel"];
  selectedVoiceId: string;
  selectedLanguage: string;
  prompt: string;
  onChangePrompt: (prompt: string) => void;
  isLoading: boolean;
  isPending: boolean;
  onChangeLanguage: (language: string) => void;
  onChangeVoice: (voiceId: string) => void;
  voices: VoiceWithPreview[];
  voiceAvatarSeed: string;
  stopPreview: () => void;
  showDownloadButton?: boolean;
  onAfterDownload?: () => void;
  ttsBaseUrlOverride?: string;
  ttsAdditionalHeaders?: Record<string, string>;
}) {
  const t = useTranslations("ClientKeys.Globals");
  const tTTS = useTranslations("ClientKeys.TTS");
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const selectedVoice = voices.find(v => v.voiceId === selectedVoiceId) ?? voices[0];
  const sortedLanguages = useMemo(() => [...languages].sort(sortLanguagesWithEnglishFirst), []);

  const [displayCharacters, setDisplayCharacters] = useState<string[]>([]);

  const {
    generate,
    playingState,
    setPlayingState,
    alignment,
    regenerate,
    setRegenerate,
    showLimitReachedOverlay,
    completeAudioData,
  } = useTtsGeneration(
    initialText,
    selectedVoice,
    1, // speed
    true, // useStreaming
    "interact_create_omnibox",
  );

  const playButtonIsDisabled = isLoading || isPending || prompt.trim() === "" || playingState === "loading";

  async function handleGenerate() {
    if (!audioRef.current) return;
    setDisplayCharacters(prompt.split(""));
    await generate({
      textPrompt: prompt,
      audioElement: audioRef.current,
      modelId: TTSModels.ELEVEN_V3,
      voice: selectedVoice,
      language: (selectedLanguage ?? undefined) as string | undefined,
      ttsBaseUrlOverride,
      ttsAdditionalHeaders,
    });
  }

  const pause = useCallback(() => {
    if (!audioRef.current) return;
    audioRef.current.pause();
    setPlayingState("paused");
  }, [setPlayingState]);

  function play() {
    if (!audioRef.current) return;
    audioRef.current.play();
    setPlayingState("playing");
  }

  function handlePlayPause() {
    if (playingState === "playing" || playingState === "loading") {
      pause();
    } else {
      play();
    }
  }

  function handlePlay() {
    stopPreview();
    if (regenerate) {
      handleGenerate();
    } else {
      handlePlayPause();
    }
  }

  // Memoize `<Select>` props to avoid re-rendering when `text` changes.
  const voiceListBoxStyle = useMemo(() => ({ minWidth: "calc(max(11rem, var(--trigger-width) + 0.5rem) + 2rem)" }), []);
  const voiceItems = useMemo(
    () =>
      voices.map((voice, voiceIndex) => {
        const [label, subLabel] = voice.name.split(/\s+-\s+/);
        const image = getVoiceAvatarImage({ index: voiceIndex, seed: voiceAvatarSeed });
        return {
          id: voice.voiceId,
          textValue: label,
          subLabel,
          image,
        };
      }),
    [voices, voiceAvatarSeed],
  );
  const onChangeVoice = useCallback(
    (key: Key | null) => {
      stopPreview();
      onChangeVoiceProp(String(key));
      setRegenerate(true);
      pause();
    },
    [pause, setRegenerate, onChangeVoiceProp, stopPreview],
  );

  // Language selector props
  const languageListBoxStyle = useMemo(() => ({ width: pxToRem(246) }), []);
  const languageItems = useMemo(
    () =>
      sortedLanguages.map(lang => ({
        id: lang.value,
        textValue: getLanguageDisplayLabel(lang),
        image: { src: getDynamicFlagURL(lang.value), width: 16, height: 16 },
      })),
    [sortedLanguages],
  );
  const onChangeLanguage = useCallback(
    (key: Key | null) => {
      setRegenerate(true);
      pause();
      onChangeLanguageProp(String(key));
    },
    [pause, setRegenerate, onChangeLanguageProp],
  );
  const languageScrollTo = useCallback(
    ({ rowHeight }: { rowHeight: number }) => {
      return languageItems.findIndex(item => item.id === selectedLanguage) * rowHeight;
    },
    [languageItems, selectedLanguage],
  );

  useImperativeHandle(handleRef, () => ({ pause, setRegenerate }));

  return (
    <div
      tabIndex={variant === "default" ? -1 : undefined}
      className={clsx(
        "tw-relative tw-flex tw-flex-col",
        variant === "default" && "tw-outline-none tw-snap-start tw-flex-none tw-w-full md:tw-w-1/2",
        size === "lg" && "tw-flex-1",
      )}
    >
      {variant === "default" && defaultMobilePanel !== "editor" && (
        <button
          ref={initialFocusRef}
          type='button'
          className={clsx(
            "tw-absolute tw-z-50 tw-left-5 tw-top-4 tw-text-xs/none tw-font-medium tw-bg-white hover:tw-bg-cream tw-text-black tw-py-2 tw-px-3 tw-rounded-full tw-outline-none focus-visible:tw-outline-focus md:tw-hidden",
          )}
          style={{ boxShadow: "0 0 1px rgb(0 0 0 / 0.4), 0 4px 4px rgb(0 0 0 / 0.04)" }}
          onClick={onBack}
        >
          {t("back")}
        </button>
      )}

      {variant === "default" && (
        <div className='tw-absolute tw-inset-x-0 tw-h-10 tw-z-50 tw-pointer-events-none tw-rounded-tr-3xl tw-border-r-0.5 tw-border-t-0.5 tw-border-gray-200 tw-hidden md:tw-block' />
      )}

      <div
        className={clsx(
          "tw-flex-auto tw-overflow-y-auto tw-scroll-pt-4 tw-scroll-pb-6 tw-flex tw-flex-col tw-scrollbar-none",
          {
            "tw-h-60": variant === "default",
            "tw-flex-1": variant !== "default" && size === "lg",
            "tw-h-40": variant !== "default" && size !== "lg",
          },
        )}
        inert={showLimitReachedOverlay}
      >
        {variant === "default" && (
          <div className='tw-z-10 tw-isolate tw-sticky tw-top-0 tw-flex-none tw-h-11 tw-pb-3 -tw-mb-3 tw-items-end tw-px-5 tw-hidden md:tw-flex tw-pointer-events-none'>
            <div className='tw-absolute -tw-z-10 tw-inset-0 tw-ml-px tw-bg-gradient-to-b tw-from-white tw-via-white tw-rounded-t-3xl md:tw-rounded-tl-none' />
            <p className='tw-min-w-0 tw-type-xs tw-text-gray-500 tw-pointer-events-auto'>
              <span className='tw-block tw-truncate'>{tTTS("widget.enter_your_own_text")}</span>
            </p>
          </div>
        )}
        <div className='tw-relative tw-flex-auto tw-flex tw-flex-col'>
          {playingState === "playing" && (
            <TextHighlighter
              className={clsx(
                "tw-isolate tw-absolute tw-inset-0 tw-text-base/base tw-px-5 tw-text-transparent",
                variant === "default"
                  ? defaultMobilePanel === "editor"
                    ? "tw-pt-3"
                    : "tw-pt-[3.75rem] md:tw-pt-3"
                  : "tw-pt-4",
              )}
              characters={alignment?.characters ?? []}
              displayCharacters={displayCharacters}
              endTimes={alignment?.character_end_times_seconds ?? []}
              mediaRef={audioRef}
              startTimes={alignment?.character_start_times_seconds ?? []}
              withBrackets
              wordClassName='tw-rounded tw-px-0.5 -tw-mx-0.5'
              wordStyle={{
                backgroundColor: { highlighted: "oklch(0.97 0.001 106.424)" },
              }}
            />
          )}
          <TextEditor
            spellCheck={false}
            aria-label={tTTS("widget.textarea_label")}
            className={clsx("tw-flex-auto", isPending && "tw-opacity-50 tw-transition")}
            textClassName={clsx(
              "tw-px-5 tw-text-base/base tw-text-black tw-caret-focus tw-outline-none",
              variant === "default"
                ? defaultMobilePanel === "editor"
                  ? "tw-pt-3"
                  : "tw-pt-[3.75rem] md:tw-pt-3"
                : "tw-pt-4",
            )}
            disabled={isLoading || isPending}
            value={prompt}
            maxLength={1000}
            onChange={text => {
              onChangePrompt(text);
              setRegenerate(true);
              pause();
            }}
          >
            {/* Make [bracketed text] gray */}
            {text =>
              text.split(/(\[[^\]\n]+\])/).map((part, index) =>
                index % 2 === 0 ? (
                  part
                ) : (
                  <span key={index} className='tw-text-gray-400'>
                    {part}
                  </span>
                ),
              )
            }
          </TextEditor>
        </div>
        <div className='tw-flex-none tw-sticky tw-bottom-0 tw-h-6 tw-bg-gradient-to-t tw-from-white tw-mx-[0.5px]' />
      </div>
      <div
        className={clsx(
          "tw-relative tw-z-10 tw-flex tw-items-center tw-px-3",
          variant === "default" ? "tw-pb-5 md:tw-pb-3" : "tw-pb-3",
        )}
        inert={showLimitReachedOverlay}
      >
        <Select
          value={selectedLanguage}
          onChange={onChangeLanguage}
          aria-label={tTTS("widget.language")}
          items={languageItems}
          virtualize
          listBoxStyle={languageListBoxStyle}
          scrollTo={languageScrollTo}
          hideSelectedLabelOnMobile
        />
        <div className={clsx(variant === "mini" ? "tw-contents" : "tw-contents md:tw-hidden")}>
          <Select
            items={voiceItems}
            value={selectedVoiceId}
            onChange={onChangeVoice}
            aria-label={tTTS("widget.voice")}
            isDisabled={isLoading || isPending}
            className={clsx("tw-min-w-0", isPending && "tw-opacity-50 tw-transition")}
            listBoxStyle={voiceListBoxStyle}
            hideSelectedImageOnMobile
          />
        </div>
        {showDownloadButton && (
          <DownloadButton
            audioElementRef={audioRef}
            canDownload={!regenerate && !!prompt && !!completeAudioData}
            completeAudioData={completeAudioData}
            buttonVariant='elevated'
            buttonSize='pill'
            className='tw-ml-0 !tw-shadow-none'
            onAfterDownload={onAfterDownload}
          />
        )}
        <button
          type='button'
          className={clsx(
            "tw-flex-none tw-ml-auto tw-bg-black tw-text-white tw-rounded-full tw-flex tw-items-center tw-justify-center tw-transition-colors tw-outline-none focus-visible:tw-outline-focus tw-text-sm/none",
            playButtonIsDisabled ? "tw-cursor-not-allowed" : "hover:tw-bg-gray-600",
            playingState === "loading" ? "tw-size-9" : "tw-h-9 tw-px-3.5",
          )}
          onClick={handlePlay}
          aria-label={playingState === "playing" ? t("pause") : t("play")}
          disabled={playButtonIsDisabled}
        >
          {playingState === "loading" ? (
            <svg
              viewBox='0 0 24 24'
              aria-hidden
              className='tw-size-4 tw-animate-spin'
              style={{ animationDuration: "0.7s" }}
            >
              <g fill='none' stroke='currentColor' strokeWidth='2.25'>
                <path strokeOpacity='.3' d='M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z' />
                <path strokeLinecap='round' d='M20.945 13A9.004 9.004 0 0 1 13 20.945' />
              </g>
            </svg>
          ) : playingState === "playing" ? (
            t("pause")
          ) : (
            t("play")
          )}
        </button>
        <audio
          ref={audioRef}
          className='tw-hidden'
          onEnded={() => setPlayingState("idle")}
          onPause={() => setPlayingState("paused")}
          onPlay={() => setPlayingState("playing")}
        />
      </div>
      <LimitReachedOverlay
        show={showLimitReachedOverlay}
        title={tTTS("widget.quota_exceeded_title")}
        description={tTTS("widget.quota_exceeded_description")}
        eventProperties={{
          product: "tts",
          limit_type: "quota",
        }}
      />
    </div>
  );
});

function SpeechInner({
  initialText,
  initialVoices,
  initialLanguage,
  variant = "default",
  size = "sm",
  hideExploreLinks = false,
  showDownloadButton = false,
  onAfterDownload,
  defaultMobilePanel = "voices",
  initialSelectedVoiceId,
  ttsBaseUrlOverride,
  ttsAdditionalHeaders,
}: SpeechProps) {
  const t = useTranslations("ClientKeys.TTS");
  const { stop: stopPreview } = useNativeAudio();

  const [text, setText] = useState(initialText);
  const [selectedLanguage, setSelectedLanguage] = useState<Key | null>(initialLanguage);
  const [voices, setVoices] = useState<VoiceWithPreview[]>(initialVoices);
  const [selectedVoiceId, setSelectedVoiceId] = useState<Key>(
    initialSelectedVoiceId ?? initialVoices[0]?.voiceId ?? "",
  );
  const voiceAvatarSeed = voices.map(v => v.voiceId).join("");

  const [isLoading, setIsLoading] = useState(false);
  const isPending = useSpinDelay(isLoading, { delay: 150, minDuration: 300 });
  const [pendingText, setPendingText] = useState<string | null>(null);
  const [pendingVoices, setPendingVoices] = useState<VoiceWithPreview[] | null>(null);
  const [pendingSelectedVoiceId, setPendingSelectedVoiceId] = useState<Key | null>(null);

  const voiceSelectorInitialFocusRef = useRef<HTMLLabelElement>(null);
  const promptEditorInitialFocusRef = useRef<HTMLButtonElement>(null);
  const promptEditorHandleRef = useRef<{ pause: () => void; setRegenerate: (regenerate: boolean) => void }>(null);

  // Use refs for initial values to prevent effect from re-running when prop references change
  const initialVoicesRef = useRef(initialVoices);
  const initialTextRef = useRef(initialText);
  const initialLanguageRef = useRef(initialLanguage);

  const onLoad = useCallback(({ prompt, voices: loadedVoices }: { prompt: string; voices: VoiceWithPreview[] }) => {
    const voices = loadedVoices.length > 0 ? loadedVoices : initialVoicesRef.current;
    setIsLoading(false);
    setPendingVoices(voices);
    setPendingSelectedVoiceId(voices[0]?.voiceId ?? "");
    setPendingText(prompt);
  }, []);

  useEffect(() => {
    if (selectedLanguage === initialLanguageRef.current) {
      return;
    }
    let current = true;
    setIsLoading(true);

    Promise.all([
      getDefaultPrompt(selectedLanguage as string, TTSModels.ELEVEN_V3),
      getVoicesWithPreview(selectedLanguage as string),
    ])
      .then(([prompt, voices]) => {
        if (!current) return;
        onLoad({ prompt: prompt.text, voices });
      })
      .catch(error => {
        if (!current) return;
        setIsLoading(false);
        logger.error(error instanceof Error ? error : null, "Error getting default prompt or voices");
      });

    return () => {
      current = false;
    };
  }, [selectedLanguage, onLoad]);

  useLayoutEffect(() => {
    if (pendingVoices && !isPending) {
      setVoices(pendingVoices);
      setPendingVoices(null);
    }
    if (pendingSelectedVoiceId && !isPending) {
      setSelectedVoiceId(pendingSelectedVoiceId);
      setPendingSelectedVoiceId(null);
    }
    if (pendingText && !isPending) {
      setText(pendingText);
      setPendingText(null);
    }
  }, [pendingVoices, pendingSelectedVoiceId, pendingText, isPending]);

  const onChangeLanguage = useCallback(
    (language: string) => {
      stopPreview();
      captureEvent(EventName.mw_tts_language_changed, {
        previous_language: selectedLanguage,
        new_language: language,
      });
      setSelectedLanguage(language);
      if (language === initialLanguageRef.current) {
        onLoad({ prompt: initialTextRef.current, voices: initialVoicesRef.current });
      }
    },
    [stopPreview, selectedLanguage, onLoad],
  );

  const onChangeVoice = useCallback(
    (voiceId: string) => {
      promptEditorHandleRef.current?.pause();
      promptEditorHandleRef.current?.setRegenerate(true);
      setSelectedVoiceId(voiceId);
      captureEvent(EventName.mw_tts_voice_changed, {
        previous_voice_id: selectedVoiceId,
        new_voice_id: voiceId,
      });
    },
    [selectedVoiceId],
  );

  const onForward = useCallback(() => {
    promptEditorInitialFocusRef.current?.focus();
    captureEvent(EventName.mw_tts_edit_text);
  }, []);

  const onBack = useCallback(() => {
    voiceSelectorInitialFocusRef.current?.focus();
  }, []);

  const onPlayPreview = useCallback((voice: VoiceWithPreview) => {
    promptEditorHandleRef.current?.pause();
    captureEvent(EventName.mw_tts_voice_heard, {
      source_component: "interact_create_omnibox",
      audio_type: "preview",
      voice_id: voice.voiceId,
      voice_name: voice.name,
      trigger: "voice_selection",
    });
  }, []);

  return (
    <div className='tw-h-full tw-flex tw-items-center'>
      <div
        className={clsx(
          "-tw-mt-9 tw-relative tw-isolate tw-bg-white tw-rounded-3xl tw-w-full tw-mx-auto tw-ring-0.5 tw-ring-inset tw-ring-gray-200",
          variant === "default" ? "tw-max-w-[30rem] md:tw-max-w-[52rem]" : "tw-max-w-[37rem]",
          size === "lg" && "tw-h-[18.75rem]",
        )}
      >
        {/* Vertical separator */}
        {variant === "default" && (
          <div className='tw-w-[0.5px] tw-bg-gray-200 tw-absolute tw-z-10 tw-inset-y-0 tw-left-1/2 tw-hidden md:tw-block' />
        )}

        <div
          className={clsx({
            "tw-flex tw-overflow-hidden tw-scroll-smooth tw-snap-x tw-snap-mandatory": variant === "default",
            "tw-flex tw-flex-col tw-h-full": size === "lg",
          })}
        >
          {variant === "default" && (
            <VoiceSelector
              initialFocusRef={voiceSelectorInitialFocusRef}
              isPending={isPending}
              isLoading={isLoading}
              selectedVoiceId={String(selectedVoiceId)}
              voices={voices}
              voiceAvatarSeed={voiceAvatarSeed}
              onForward={onForward}
              onPlayPreview={onPlayPreview}
              onChangeVoice={onChangeVoice}
              hideExploreLinks={hideExploreLinks}
              defaultMobilePanel={defaultMobilePanel}
            />
          )}
          <PromptEditor
            initialFocusRef={promptEditorInitialFocusRef}
            handleRef={promptEditorHandleRef}
            onBack={onBack}
            initialText={initialText}
            variant={variant}
            size={size}
            defaultMobilePanel={defaultMobilePanel}
            selectedVoiceId={String(selectedVoiceId)}
            voices={voices}
            selectedLanguage={String(selectedLanguage)}
            prompt={text}
            onChangePrompt={setText}
            isLoading={isLoading}
            isPending={isPending}
            voiceAvatarSeed={voiceAvatarSeed}
            onChangeLanguage={onChangeLanguage}
            onChangeVoice={onChangeVoice}
            stopPreview={stopPreview}
            showDownloadButton={showDownloadButton}
            onAfterDownload={onAfterDownload}
            ttsBaseUrlOverride={ttsBaseUrlOverride}
            ttsAdditionalHeaders={ttsAdditionalHeaders}
          />
        </div>
        {variant === "default" && !hideExploreLinks && (
          <Footer
            link={{ href: appUrls.voiceLibrary, eventName: EventName.mw_tts_voice_library_explore_clicked }}
            className='md:tw-hidden'
          >
            {t("widget.cta")}
          </Footer>
        )}
      </div>
    </div>
  );
}

export { SpeechInner };

export function Speech(props: SpeechProps) {
  return (
    <NativeAudioProvider>
      <SpeechInner {...props} />
    </NativeAudioProvider>
  );
}
